import React, { useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Typography } from '@mui/material';
import { PersonSearch as PersonSearchIcon } from '@mui/icons-material';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useOrder } from '../hooks/useOrder';
import { useConfirm } from '../contexts/FeedbackContext';
import OrderHeader from '../components/orders/OrderHeader';
import ProductSelector from '../components/orders/ProductSelector';
import OrderItemsTable from '../components/orders/OrderItemsTable';
import OrderSidebar from '../components/orders/OrderSidebar';
import ClientPicker from '../components/orders/ClientPicker';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const NewOrder = ({ orderType: orderTypeProp }) => {
  const { logout, user } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { id: editOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('clientId');
  const isEditMode = Boolean(editOrderId);

  const {
    order, orderItems, loading, error, success,
    restoreOrCreateOrder, fetchOrder,
    addItem, updateItem, removeItem, updateOrder, setClient,
    validateOrder,
    autoSaving
  } = useOrder();

  useEffect(() => {
    if (isEditMode) {
      // Edición de un pedido existente: se carga por id (ej: al volver desde la revisión).
      fetchOrder(editOrderId);
    } else {
      // Un cliente mayorista siempre pide para sí mismo: no se le quita el cliente.
      restoreOrCreateOrder(orderTypeProp, { resetClient: !user?.is_wholesale_client });
    }
  }, [isEditMode, editOrderId, fetchOrder, restoreOrCreateOrder, orderTypeProp, user?.is_wholesale_client]);

  // Al crear, el canal viene de la ruta (público/mayorista).
  // Al editar, se deriva del pedido cargado.
  const orderType = isEditMode
    ? (order?.order_type === 'wholesale' ? 'business' : 'public')
    : orderTypeProp;

  // Sin cliente no se puede cargar nada: los precios dependen de su lista.
  // Un cliente mayorista logueado siempre pide para sí mismo.
  const needsClient = Boolean(order) && !order.client && !user?.is_wholesale_client;
  const lockedSx = needsClient ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none', filter: 'grayscale(0.4)' } : {};

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading && !order) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
          <CircularProgress />
          <Typography color="textSecondary">Cargando pedido...</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !order) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', height: { md: '100vh' }, overflow: { md: 'hidden' }, bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' } }}>

        <OrderHeader
          order={order}
          cancelLabel={isEditMode ? 'Volver al listado' : 'Cancelar'}
          onCancel={async () => {
            const base = orderType === 'business' ? '/orders/business' : '/orders/public';
            // Al editar, cada cambio ya se guarda al instante: solo se vuelve al listado
            if (isEditMode) {
              navigate(`${base}/list`);
            } else if (await confirm({ title: 'Cancelar pedido', message: 'Se descartará el pedido en curso. ¿Querés cancelarlo?', confirmText: 'Cancelar pedido', cancelText: 'Seguir editando', danger: true })) {
              window.location.href = base;
            }
          }}
        />

        {!user?.is_wholesale_client && (
          <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e7edf3', px: 3, py: 1.5 }}>
            <Box sx={{
              border: order?.client ? '1px solid #e7edf3' : '2px solid #1976d2',
              borderRadius: 2, px: 2, py: 1.5, bgcolor: order?.client ? '#fafbfc' : 'white',
            }}>
              <ClientPicker
                client={order?.client}
                orderType={orderType}
                deliveryDate={order?.delivery_date}
                itemsCount={orderItems.length}
                onSelect={(client) => setClient(client.id)}
              />
            </Box>
          </Box>
        )}

        {(error || success || autoSaving) && (
          <Box sx={{ px: 3, pt: 2 }}>
            {error && <Alert severity="error" onClose={() => { }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => { }}>{success}</Alert>}
            {autoSaving && <Typography variant="caption" color="textSecondary">Guardando...</Typography>}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0, overflow: { xs: 'visible', md: 'hidden' }, p: 2, position: 'relative' }}>
          {needsClient && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', justifyContent: 'center', alignItems: { xs: 'flex-start', md: 'center' }, pt: { xs: 6, md: 0 }, pointerEvents: 'none' }}>
              <Box sx={{ bgcolor: 'white', border: '1px solid #90caf9', borderRadius: 2, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 4px 16px rgba(14, 20, 27, 0.08)', maxWidth: 420, mx: 2 }}>
                <PersonSearchIcon sx={{ color: '#1976d2', fontSize: 32 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>Primero elegí el cliente</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#4e7397' }}>
                    Los productos y precios dependen de su lista de precios.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Grid container spacing={2} aria-disabled={needsClient || undefined} sx={{ height: { xs: 'auto', md: '100%' }, transition: 'opacity 0.2s', ...lockedSx }}>
            {/* Left: Product Selector */}
            <Grid item xs={12} md={7} lg={8} sx={{ minWidth: 0, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' } }}>
              <Box sx={{ height: { xs: '70vh', md: '100%' }, bgcolor: 'white', borderRadius: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e7edf3' }}>
                <ProductSelector
                  clientId={order?.client?.id || clientIdFromUrl}
                  channel={orderType === 'business' ? 'wholesale' : 'retail'}
                  onAddProduct={(product, qty) => addItem(product.id, qty, product.price_per_kg)}
                />
              </Box>
            </Grid>

            {/* Right: Items and Sidebar */}
            <Grid item xs={12} md={5} lg={4} sx={{ minWidth: 0, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ flex: 1, minHeight: { md: 240 }, overflowY: 'auto', bgcolor: 'white', borderRadius: 1, border: '1px solid #e7edf3' }}>
                <OrderItemsTable
                  items={orderItems}
                  onUpdateItem={updateItem}
                  onRemoveItem={removeItem}
                />
              </Box>
              <Box sx={{ flexShrink: 1, minHeight: 0, overflowY: 'auto', bgcolor: 'white', borderRadius: 1, border: '1px solid #e7edf3' }}>
                <OrderSidebar
                  order={order}
                  items={orderItems}
                  orderType={orderType}
                  onValidate={validateOrder}
                  deliveryDate={order?.delivery_date}
                  setDeliveryDate={(date) => updateOrder({ delivery_date: date })}
                  specialProduction={order?.special_production}
                  setSpecialProduction={(val) => updateOrder({ special_production: val })}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default NewOrder;
